import { useState, useCallback } from 'react';
import { useSignUp, useUser } from '@clerk/clerk-react';

export const useAuthentication = () => {
	const { signUp, isLoaded: isSignUpLoaded, setActive } = useSignUp();
	const { isSignedIn } = useUser();

	const [isAuthenticating, setIsAuthenticating] = useState(false);
	const [authError, setAuthError] = useState(null);

	const handleEmailPasswordSignUp = useCallback(
		async (email, password, fullName) => {
			if (!isSignUpLoaded) return { success: false };

			try {
				setIsAuthenticating(true);
				setAuthError(null);

				// Split full name into first and last name
				const nameParts = fullName.trim().split(' ');
				const firstName = nameParts[0] || '';
				const lastName = nameParts.slice(1).join(' ') || '';

				// Create the sign-up
				const result = await signUp.create({
					emailAddress: email,
					password,
					firstName,
					lastName,
					unsafeMetadata: {
						product: 'unify'
					}
				});

				// Check if email verification is required
				if (result.status === 'missing_requirements') {
					// Prepare email verification
					await signUp.prepareEmailAddressVerification({
						strategy: 'email_code'
					});

					setIsAuthenticating(false);
					return { success: true, verificationRequired: true };
				}

				// If complete, set the session
				if (result.status === 'complete') {
					await setActive({ session: result.createdSessionId });
					setIsAuthenticating(false);
					return { success: true, verificationRequired: false };
				}

				setIsAuthenticating(false);
				return { success: false, error: 'Unexpected sign-up status', userId: result.createdUserId };
			} catch (err) {
				console.error('Email/password sign-up error:', err);
				setIsAuthenticating(false);

				// Handle specific Clerk errors
				if (err.errors?.[0]?.code === 'form_identifier_exists') {
					setAuthError('This email is already registered. Please sign in instead.');
					alert('This email is already registered. Please sign in instead.');
				} else if (err.errors?.[0]?.code === 'form_password_pwned') {
					setAuthError('This password has been found in a data breach. Please choose a different password.');
					alert('This password has been found in a data breach. Please choose a different password.');
				} else if (err.errors?.[0]?.code === 'form_password_length_too_short') {
					setAuthError('Password must be at least 8 characters.');
					alert('Password must be at least 8 characters.');
				} else if (err.errors?.[0]?.message) {
					setAuthError(err.errors[0].message);
					alert(err.errors[0].message);
				} else {
					setAuthError('Sign-up failed. Please try again.');
					alert('Sign-up failed. Please try again.');
				}

				return { success: false, error: err.errors?.[0]?.message || 'Sign-up failed' };
			}
		},
		[signUp, isSignUpLoaded, setActive]
	);

	const clearAuthError = useCallback(() => {
		setAuthError(null);
	}, []);

	return {
		// State
		isSignedIn,
		isAuthenticating,
		authError,

		// Methods

		handleEmailPasswordSignUp,
		clearAuthError
	};
};
